<script setup lang="ts">
import { ref } from 'vue'
import { Avatar } from '@ficpod/ui-components'

export interface NavLink {
  to: string
  label: string
  show?: boolean
}

export interface HeaderProps {
  brandName: string
  brandTo?: string
  navLinks?: NavLink[]
  showAdmin?: boolean
  adminTo?: string
  profileTo?: string
  signOutTo?: string
  loginTo?: string
  signupTo?: string
}

withDefaults(defineProps<HeaderProps>(), {
  brandTo: '/',
  navLinks: () => [],
  showAdmin: false,
  adminTo: '/admin',
  profileTo: '/profile',
  signOutTo: '/',
  loginTo: '/login',
  signupTo: '/signup'
})

const authStore = defineModel<any>('authStore', { required: true })
const showMobileMenu = ref(false)

const emit = defineEmits<{
  (e: 'signOut'): void
}>()

async function handleSignOut() {
  emit('signOut')
}
</script>

<template>
  <header class="header">
    <nav class="header__nav">
      <div class="header__inner">
        <router-link :to="brandTo" class="header__brand">
          {{ brandName }}
        </router-link>
          
        <div class="header__nav-links">
          <router-link 
	    v-for="link in navLinks" 
            :key="link.to"
            :to="link.to" 
            class="header__nav-link"
            v-show="link.show !== false"
          >
	  {{ link.label }}
          </router-link>
        </div>

        <div class="header__actions">
          <template v-if="authStore.loading">
            <div class="animate-pulse bg-burgundy-muted h-8 w-20 rounded"></div>
          </template>
          <template v-else-if="authStore.user">
            <slot name="user-actions">
              <router-link v-if="showAdmin && authStore.user.is_admin" :to="adminTo" class="header__nav-link">Admin</router-link>
              <router-link :to="profileTo" class="flex items-center gap-2 header__nav-link">
                <Avatar :profile="authStore.user" :show-badge="true" />
                <span>{{ authStore.user.display_name || authStore.user.username }}</span>
              </router-link>
              <button 
                @click="handleSignOut"
                class="btn btn--secondary"
              >
                Sign Out
              </button>
            </slot>
          </template>
          <template v-else>
            <slot name="guest-actions">
              <router-link :to="loginTo" class="btn btn--ghost">
                Sign In
              </router-link>
              <router-link :to="signupTo" class="btn btn--secondary">
                Sign Up
              </router-link>
            </slot>
          </template>
        </div>

        <div class="header__mobile-actions">
          <template v-if="authStore.loading">
            <div class="animate-pulse bg-burgundy-muted h-8 w-8 rounded-full"></div>
          </template>
          <template v-else-if="authStore.user">
            <router-link :to="profileTo" class="flex items-center gap-2">
              <Avatar :profile="authStore.user" :show-badge="true" />
            </router-link>
          </template>
          
          <button 
            class="header__menu-toggle"
            @click="showMobileMenu = !showMobileMenu"
            aria-label="Toggle menu"
          >
            <svg class="header__menu-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path v-if="showMobileMenu" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
      </div>

      <div v-if="showMobileMenu" class="header__mobile-menu">
        <div class="header__mobile-menu-inner">
          <router-link 
            v-for="link in navLinks" 
            :key="link.to"
            :to="link.to" 
            class="header__nav-link"
            @click="showMobileMenu = false"
            v-show="link.show !== false"
          >
            {{ link.label }}
          </router-link>
          
          <template v-if="authStore.user">
            <router-link v-if="showAdmin && authStore.user.is_admin" :to="adminTo" class="header__nav-link" @click="showMobileMenu = false">Admin</router-link>
            <router-link :to="profileTo" class="header__nav-link" @click="showMobileMenu = false">Profile</router-link>
            <button 
              @click="handleSignOut"
              class="header__nav-link text-left"
            >
              Sign Out
            </button>
          </template>
          <template v-else>
            <router-link :to="loginTo" class="header__nav-link" @click="showMobileMenu = false">Sign In</router-link>
            <router-link :to="signupTo" class="header__nav-link" @click="showMobileMenu = false">Sign Up</router-link>
          </template>
        </div>
      </div>
    </nav>
  </header>
</template>
